'use client'

import { Fragment, type ReactNode } from 'react'
import { TriangleAlert } from 'lucide-react'

import { cn } from '@/lib/utils'
import { CardGrid } from './card-grid'
import { CardGridSkeleton } from './card-grid-skeleton'
import { DataPagination } from './data-pagination'
import { EmptyState } from './empty-state'
import {
  SearchFilterBar,
  type FilterDef,
  type SearchFilterState,
} from './search-filter-bar'

export type DataListStatus = 'loading' | 'error' | 'success'

export type DataListProps<T> = {
  // Filter bar (controlled — pass state + handler, e.g. from useListQueryState)
  filters: FilterDef[]
  filterState: SearchFilterState
  onFilterChange: (state: SearchFilterState) => void
  searchPlaceholder?: string

  // Data
  status?: DataListStatus
  items: T[]
  getKey: (item: T, index: number) => string
  renderItem: (item: T) => ReactNode

  /** Rendered as the first cell of the grid on the success state (e.g. a featured card). */
  featured?: ReactNode

  // Pagination (1-based)
  page?: number
  pageCount?: number
  onPageChange?: (page: number) => void

  // State copy
  skeletonCount?: number
  emptyTitle?: string
  emptyDescription?: string
  errorTitle?: string

  gridClassName?: string
  className?: string
}

/**
 * The reusable directory pattern: filter bar + responsive card grid + pagination,
 * with the four render states (loading / error / empty / success) handled distinctly.
 * Presentational — pass in items/status/page; filtering, sorting and paging happen
 * server-side (mp-core), never here.
 */
export function DataList<T>({
  filters,
  filterState,
  onFilterChange,
  searchPlaceholder,
  status = 'success',
  items,
  getKey,
  renderItem,
  featured,
  page = 1,
  pageCount = 1,
  onPageChange,
  skeletonCount = 6,
  emptyTitle = 'No results found',
  emptyDescription = 'Try adjusting your search or filters.',
  errorTitle = 'Something went wrong',
  gridClassName = "mb-12",
  className,
}: DataListProps<T>) {
  return (
    <div className={cn('space-y-grid', className)}>
      <SearchFilterBar
        filters={filters}
        value={filterState}
        onChange={onFilterChange}
        searchPlaceholder={searchPlaceholder}
      />

      {status === 'loading' ? (
        <CardGridSkeleton count={skeletonCount} className={gridClassName} />
      ) : status === 'error' ? (
        <EmptyState
          icon={TriangleAlert}
          title={errorTitle}
          description="Please try again in a moment."
        />
      ) : items.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <>
          <CardGrid className={gridClassName}>
            {featured}
            {items.map((item, i) => (
              <Fragment key={getKey(item, i)}>{renderItem(item)}</Fragment>
            ))}
          </CardGrid>
          {onPageChange && (
            <DataPagination page={page} pageCount={pageCount} onPageChange={onPageChange} />
          )}
        </>
      )}
    </div>
  )
}
